import type {
  IAssetTemplateAssignedResult,
  IAssetTemplateAssignedFilterResult,
  IAssetTemplateAssignedPickerResult,
} from '../hooks';
import type { IBaseCampaign } from '~/modules/campaign/types';
import type { IAssetTemplateAssigned } from '../types';
import type { ReactNode } from 'react';

import {
  useInitializeAssetTemplateAssigned,
  useInitializeAssetTemplateAssignedFilter,
  useInitializeAssetTemplateAssignedPicker,
} from '../hooks';
import { createContext, useContext, useMemo } from 'react';

export interface IAssetTemplateAssignedContext
  extends
    IAssetTemplateAssignedResult,
    IAssetTemplateAssignedFilterResult,
    IAssetTemplateAssignedPickerResult {
  campaigns: IBaseCampaign[];
  listPageAssigned?: IAssetTemplateAssigned[];
}

export interface AssetTemplateAssignedProviderProps {
  children: ReactNode;
  campaigns: IBaseCampaign[];
}

export const AssetTemplateAssignedContext = createContext<
  IAssetTemplateAssignedContext | undefined
>(undefined);

export const AssetTemplateAssignedProvider: React.FC<AssetTemplateAssignedProviderProps> = ({
  children,
  campaigns,
}) => {
  const { listPageAssigned, ...assetTemplateAssigned } = useInitializeAssetTemplateAssigned();

  const assetTemplateAssignedFilter = useInitializeAssetTemplateAssignedFilter(
    listPageAssigned || [],
  );
  const assetTemplateAssignedPicker = useInitializeAssetTemplateAssignedPicker({
    campaigns,
    pageAssignedData: assetTemplateAssignedFilter.pageAssignedData,
    allPageAssignedDataIds: assetTemplateAssignedFilter.allPageAssignedDataIds,
  });

  const contextValue = useMemo(
    () => ({
      campaigns,
      listPageAssigned,
      ...assetTemplateAssigned,
      ...assetTemplateAssignedPicker,
      ...assetTemplateAssignedFilter,
    }),
    [
      campaigns,
      listPageAssigned,
      assetTemplateAssigned,
      assetTemplateAssignedPicker,
      assetTemplateAssignedFilter,
    ],
  );

  return (
    <AssetTemplateAssignedContext.Provider value={contextValue}>
      {children}
    </AssetTemplateAssignedContext.Provider>
  );
};

export const useAssetTemplateAssignedContext = () => {
  const context = useContext(AssetTemplateAssignedContext);
  if (!context) {
    throw new Error(
      'useAssetTemplateAssignedContext must be used within a AssetTemplateAssignedProvider',
    );
  }
  return context;
};
